import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { buildsApi } from '../../api/builds';
import { storedFilesApi } from '../../api/storedFiles';
import type { VMProtectSettings, Engine, PEAnalysis, PdbFunction } from '../../api/builds';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FunctionsTab, type SelectedFunction, type ProtectionType } from './components/FunctionsTab';
import { DisassemblySidebar } from './components/DisassemblySidebar';
import { 
  Upload, 
  Check, 
  X, 
  Shield, 
  Zap, 
  FileCode,
  Bug,
  Cpu,
  Lock,
  Fingerprint,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Loader,
  ChevronDown,
  Sparkles,
  Layers,
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
  Info,
  Search,
  ChevronRight,
  Binary,
  HardDrive,
  Code,
  FileSymlink,
  Save,
  Download,
  FileUp,
} from 'lucide-react';
import { clsx } from 'clsx';

// Engine definitions
type EngineType = 'vmprotect' | 'themida' | 'ryujin';

interface EngineDisplayInfo {
  id: EngineType;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

// Logo components
const VMProtectLogo = ({ className }: { className?: string }) => (
  <img 
    src="https://downloadlynet.ir/wp-content/uploads/2022/05/VMProtect.png" 
    alt="VMProtect" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 8px rgba(15, 240, 252, 0.5))' }}
  />
);

const ThemidaLogo = ({ className }: { className?: string }) => (
  <img 
    src="https://www.oreans.com/assets/images/Themida.png" 
    alt="Themida" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.5))' }}
  />
);

// Static display info for engines (icons, descriptions)
const ENGINE_DISPLAY: Record<string, EngineDisplayInfo> = {
  vmprotect: {
    id: 'vmprotect',
    name: 'VMProtect',
    description: 'Advanced virtualization and mutation protection',
    icon: <VMProtectLogo className="w-8 h-8 object-contain" />,
    color: 'cyan',
  },
  themida: {
    id: 'themida',
    name: 'Themida',
    description: 'Anti-cracking and anti-reverse engineering',
    icon: <ThemidaLogo className="w-8 h-8 object-contain" />,
    color: 'purple',
  },
  ryujin: {
    id: 'ryujin',
    name: 'Ryujin',
    description: 'Custom obfuscation engine',
    icon: <span className="text-3xl">🐉</span>,
    color: 'pink',
  },
};

// Fallback display info for unknown engines
const DEFAULT_ENGINE_DISPLAY: EngineDisplayInfo = {
  id: 'vmprotect' as EngineType,
  name: 'Unknown Engine',
  description: 'Protection engine',
  icon: <Shield className="w-8 h-8 text-slate-400" />,
  color: 'slate',
};

// Debugger protection modes
type DebuggerMode = 'none' | 'usermode' | 'kernelmode';

export function NewBuildPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'upload' | 'analyzing' | 'configure' | 'processing'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [storedFileId, setStoredFileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buildStatus, setBuildStatus] = useState<string>('PENDING');
  const [dragActive, setDragActive] = useState(false);
  const [zipPassword, setZipPassword] = useState(false);
  const [generatedZipPassword, setGeneratedZipPassword] = useState<string | null>(null);

  // Available engines from API
  const [availableEngines, setAvailableEngines] = useState<Engine[]>([]);
  const [enginesLoading, setEnginesLoading] = useState(true);

  // Engine selection
  const [selectedEngine, setSelectedEngine] = useState<EngineType>('vmprotect');
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);

  // Debugger mode (mutually exclusive)
  const [debuggerMode, setDebuggerMode] = useState<DebuggerMode>('none');

  // PE Analysis
  const [peAnalysis, setPeAnalysis] = useState<PEAnalysis | null>(null);
  const [peLoading, setPeLoading] = useState(false);
  const [peError, setPeError] = useState<string | null>(null);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<'overview' | 'sections' | 'imports' | 'exports'>('overview');
  const [importSearchQuery, setImportSearchQuery] = useState('');
  const [exportSearchQuery, setExportSearchQuery] = useState('');
  const [expandedDlls, setExpandedDlls] = useState<Set<string>>(new Set());

  // Configure page tabs
  const [activeConfigTab, setActiveConfigTab] = useState<'analysis' | 'functions' | 'protection'>('analysis');

  // PDB file state
  const [pdbFile, setPdbFile] = useState<File | null>(null);
  const [pdbUploading, setPdbUploading] = useState(false);
  const [pdbUploaded, setPdbUploaded] = useState(false);
  const [pdbError, setPdbError] = useState<string | null>(null);
  const [pdbFunctionsCount, setPdbFunctionsCount] = useState<number | null>(null);

  // Selected functions for protection
  const [selectedFunctions, setSelectedFunctions] = useState<SelectedFunction[]>([]);

  // Disassembly sidebar
  const [disasmSidebarOpen, setDisasmSidebarOpen] = useState(false);
  const [disasmFunction, setDisasmFunction] = useState<PdbFunction | null>(null);

  // VMProtect settings
  const [settings, setSettings] = useState<VMProtectSettings>({
    packOutput: true,
    importProtection: true,
    memoryProtection: true,
    resourceProtection: false,
    debuggerUsermode: false,
    debuggerKernelmode: false,
    vmDetection: false,
    stripDebug: true,
    stripRelocations: false,
    shadowStack: false,
    randomizeTimestamp: true,  // Randomize PE header timestamp by default
    vmComplexity: 1,
    vmVersion: 0, // 0 = Default, 1 = Compact
    vmInstances: 1, // 1-10
    vmSectionName: '.cipher',
    useWatermark: false, // Disabled by default
    segmentExclusions: [],
    // VMProtect messages
    messages: {
      debugger: '',
      virtualTools: '',
      corrupted: '',
      serialRequired: '',
      hwidMismatch: '',
    },
  });

  // VMP Config import
  const [vmpImporting, setVmpImporting] = useState(false);
  const [vmpImportError, setVmpImportError] = useState<string | null>(null);
  const vmpFileInputRef = React.useRef<HTMLInputElement>(null);

  // Load available engines from API
  useEffect(() => {
    const loadEngines = async () => {
      try {
        const engines = await buildsApi.getEngines();
        setAvailableEngines(engines);
        
        // Set default version for the selected engine
        const vmprotectEngines = engines.filter(e => e.type === 'vmprotect');
        if (vmprotectEngines.length > 0) {
          const defaultEngine = vmprotectEngines.find(e => e.isDefault) || vmprotectEngines[0];
          setSelectedVersion(defaultEngine.version);
        }
      } catch (err) {
        console.error('Failed to load engines:', err);
      } finally {
        setEnginesLoading(false);
      }
    };
    loadEngines();
  }, []);

  // Load stored file if storedFileId is provided in URL
  useEffect(() => {
    const storedFileIdParam = searchParams.get('storedFileId');
    if (storedFileIdParam) {
      const loadStoredFile = async () => {
        setLoading(true);
        try {
          const storedFile = await storedFilesApi.get(storedFileIdParam);
          setStoredFileId(storedFileIdParam);
          setPeAnalysis(storedFile.peAnalysis);
          
          // Set up fake file object for display
          setFile({
            name: storedFile.filename,
            size: storedFile.size,
          } as File);
          
          // If stored file has PDB, set PDB state
          if (storedFile.hasPdb) {
            setPdbUploaded(true);
            setPdbFunctionsCount(storedFile.functionsCount || 0);
          }
          
          // Load saved settings if available
          if (storedFile.savedEngine) {
            setSelectedEngine(storedFile.savedEngine as EngineType);
          }
          if (storedFile.savedEngineVersion) {
            setSelectedVersion(storedFile.savedEngineVersion);
          }
          if (storedFile.savedSettings) {
            setSettings(storedFile.savedSettings);
            
            // Set debugger mode based on saved settings (mutually exclusive)
            if (storedFile.savedSettings.debuggerKernelmode) {
              setDebuggerMode('kernelmode');
            } else if (storedFile.savedSettings.debuggerUsermode) {
              setDebuggerMode('usermode');
            } else {
              setDebuggerMode('none');
            }
          }
          if (storedFile.savedFunctions && storedFile.savedFunctions.length > 0) {
            setSelectedFunctions(storedFile.savedFunctions);
          }
          
          // Skip to configure step
          setStep('configure');
        } catch (err: unknown) {
          const axiosError = err as { response?: { data?: { error?: string } } };
          setError(axiosError.response?.data?.error || 'Failed to load stored file');
        } finally {
          setLoading(false);
        }
      };
      loadStoredFile();
    }
  }, [searchParams]);

  // Get versions for the selected engine type
  const getVersionsForEngine = (engineType: string): string[] => {
    return availableEngines
      .filter(e => e.type === engineType)
      .map(e => e.version);
  };

  // Check if an engine type is available
  const isEngineAvailable = (engineType: string): boolean => {
    return availableEngines.some(e => e.type === engineType);
  };

  // Update selected version when engine changes
  const handleEngineChange = (engineId: EngineType) => {
    setSelectedEngine(engineId);
    const versions = getVersionsForEngine(engineId);
    if (versions.length > 0) {
      // Find default version or use first
      const engineList = availableEngines.filter(e => e.type === engineId);
      const defaultEngine = engineList.find(e => e.isDefault) || engineList[0];
      setSelectedVersion(defaultEngine?.version || versions[0]);
    } else {
      setSelectedVersion('');
    }
  };

  // Get display info for the current engine
  const currentEngineDisplay = ENGINE_DISPLAY[selectedEngine] || DEFAULT_ENGINE_DISPLAY;
  const currentVersions = getVersionsForEngine(selectedEngine);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files?.[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  // Just select the file, don't upload yet
  const handleFileSelect = (selectedFile: File) => {
    const validExtensions = ['.exe', '.dll', '.sys'];
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(ext)) {
      setError('Invalid file type. Only .exe, .dll, and .sys files are allowed.');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  // Start the actual upload process
  const handleStartUpload = async () => {
    if (!file) return;

    setLoading(true);
    setPeAnalysis(null);
    setPeError(null);

    try {
      const result = await buildsApi.upload(file);
      setUploadId(result.uploadId);
      
      // Move to analyzing step (loading screen)
      setStep('analyzing');
      setLoading(false);
      
      // Analyze the PE file
      setPeLoading(true);
      try {
        const analysis = await buildsApi.analyze(result.uploadId);
        setPeAnalysis(analysis);
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { error?: string } } };
        setPeError(axiosError.response?.data?.error || 'Failed to analyze PE file');
      } finally {
        setPeLoading(false);
      }

      // Upload PDB file if one was selected
      if (pdbFile) {
        setPdbUploading(true);
        try {
          const pdbResult = await buildsApi.uploadPdb(result.uploadId, pdbFile);
          setPdbUploaded(true);
          setPdbFunctionsCount(pdbResult.functionsCount);
        } catch (err: unknown) {
          const axiosError = err as { response?: { data?: { error?: string } } };
          setPdbError(axiosError.response?.data?.error || 'Failed to upload PDB file');
          // Don't fail the whole flow if PDB upload fails
        } finally {
          setPdbUploading(false);
        }
      }

      // Advance to configure step
      setStep('configure');
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Upload failed');
      setLoading(false);
    }
  };

  // Handle PDB file upload
  const handlePdbUpload = async (pdbFileToUpload: File) => {
    if (!uploadId) return;

    // Validate extension
    if (!pdbFileToUpload.name.toLowerCase().endsWith('.pdb')) {
      setPdbError('Only .pdb files are allowed');
      return;
    }

    setPdbFile(pdbFileToUpload);
    setPdbUploading(true);
    setPdbError(null);

    try {
      const result = await buildsApi.uploadPdb(uploadId, pdbFileToUpload);
      setPdbUploaded(true);
      setPdbFunctionsCount(result.functionsCount);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setPdbError(axiosError.response?.data?.error || 'Failed to upload PDB file');
      setPdbFile(null);
    } finally {
      setPdbUploading(false);
    }
  };

  // Handle opening disassembly sidebar
  const handleOpenDisassembly = (func: PdbFunction) => {
    setDisasmFunction(func);
    setDisasmSidebarOpen(true);
  };

  // Convert protection type to VMProtect compilation type
  const protectionTypeToCompilationType = (type: ProtectionType): number => {
    switch (type) {
      case 'virtualization': return 0;
      case 'mutation': return 1;
      case 'ultra': return 2;
      default: return 2;
    }
  };

  // Save current settings to the stored file for future reuse
  const handleSaveSettings = async () => {
    if (!storedFileId) return;

    setSavingSettings(true);
    setSettingsSaved(false);
    try {
      // Apply debugger mode to settings before saving
      // debuggerMode is mutually exclusive: 'none', 'usermode', or 'kernelmode'
      const settingsToSave = {
        ...settings,
        debuggerUsermode: debuggerMode === 'usermode',
        debuggerKernelmode: debuggerMode === 'kernelmode',
      };
      
      await storedFilesApi.saveSettings(storedFileId, {
        engine: selectedEngine,
        engineVersion: selectedVersion,
        settings: settingsToSave,
        functions: selectedFunctions,
      });
      setSettingsSaved(true);
      // Reset saved indicator after 3 seconds
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Export current configuration to JSON file
  const handleExportConfig = () => {
    const config = {
      version: 1,
      engine: selectedEngine,
      engineVersion: selectedVersion,
      settings: settings,
      functions: selectedFunctions,
      debuggerMode: debuggerMode,
      zipPassword: zipPassword,
      exportedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cipherbin-config-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import configuration from JSON file
  const handleImportConfig = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const config = JSON.parse(e.target?.result as string);
        
        // Validate config version
        if (!config.version || config.version !== 1) {
          setError('Invalid config file version');
          return;
        }

        // Apply config
        if (config.engine) {
          setSelectedEngine(config.engine as EngineType);
        }
        if (config.engineVersion) {
          setSelectedVersion(config.engineVersion);
        }
        if (config.settings) {
          setSettings(config.settings);
          
          // If no explicit debuggerMode, derive it from settings (mutually exclusive)
          if (!config.debuggerMode) {
            if (config.settings.debuggerKernelmode) {
              setDebuggerMode('kernelmode');
            } else if (config.settings.debuggerUsermode) {
              setDebuggerMode('usermode');
            } else {
              setDebuggerMode('none');
            }
          }
        }
        if (config.functions && Array.isArray(config.functions)) {
          setSelectedFunctions(config.functions);
        }
        if (config.debuggerMode) {
          setDebuggerMode(config.debuggerMode as DebuggerMode);
        }
        if (typeof config.zipPassword === 'boolean') {
          setZipPassword(config.zipPassword);
        }

        // Clear error and show success
        setError(null);
      } catch {
        setError('Failed to parse config file');
      }
    };
    reader.readAsText(file);
    
    // Reset file input so the same file can be loaded again
    event.target.value = '';
  };

  const handleStartBuild = async () => {
    if (!uploadId && !storedFileId) return;

    setLoading(true);
    setError(null);

    // Convert selected functions to procedures
    // When PDB is available, use demangled function names (VMProtect format: functionName(params))
    // Fall back to RVA addresses if no PDB or name available
    const procedures = selectedFunctions.map(func => ({
      // Use demangled name for VMProtect (e.g., "fibonacci(int)" or "xor_buffer(unsigned char *,int,unsigned char)")
      // Demangled name is in VMProtect-compatible format
      // Fall back to raw name, then to RVA if neither available
      mapAddress: pdbUploaded 
        ? (func.demangledName || func.name || `0x${func.rva.toString(16).toUpperCase()}`)
        : `0x${func.rva.toString(16).toUpperCase()}`,
      compilationType: protectionTypeToCompilationType(func.protectionType),
      complexity: func.complexity, // Only used for virtualization and ultra
      lockToSerial: false,
      includedInCompilation: true,
    }));

    // Update debugger settings based on mode
    const finalSettings = {
      ...settings,
      debuggerUsermode: debuggerMode === 'usermode',
      debuggerKernelmode: debuggerMode === 'kernelmode',
      procedures: procedures.length > 0 ? procedures : undefined,
    };

    try {
      const result = await buildsApi.start({
        uploadId: storedFileId ? undefined : uploadId!,
        storedFileId: storedFileId || undefined,
        settings: finalSettings,
        engineType: selectedEngine,
        engineVersion: selectedVersion,
        zipPassword: zipPassword,
      });
      
      // Store the generated zip password if returned
      if (result.zipPassword) {
        setGeneratedZipPassword(result.zipPassword);
      }
      
      setStep('processing');
      pollBuildStatus(result.id);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Failed to start build');
      setLoading(false);
    }
  };

  const pollBuildStatus = async (id: string) => {
    const poll = async () => {
      try {
        const status = await buildsApi.getStatus(id);
        setBuildStatus(status.status);

        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          setLoading(false);
          if (status.status === 'COMPLETED') {
            setTimeout(() => navigate(`/builds/${id}`), 2000);
          }
          return;
        }

        setTimeout(poll, 1000);
      } catch (err) {
        console.error('Poll error:', err);
        setTimeout(poll, 2000);
      }
    };

    poll();
  };

  const toggleSetting = (key: keyof VMProtectSettings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle VMP config file import
  const handleVmpImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const vmpFile = e.target.files?.[0];
    if (!vmpFile) return;

    setVmpImporting(true);
    setVmpImportError(null);

    try {
      const result = await buildsApi.parseVmpConfig(vmpFile);
      const { settings: vmpSettings, raw } = result;

      // Apply the parsed settings to our state
      setSettings(prev => ({
        ...prev,
        packOutput: vmpSettings.packOutputFile,
        importProtection: vmpSettings.importProtection,
        memoryProtection: vmpSettings.memoryProtection,
        resourceProtection: vmpSettings.resourceProtection,
        debuggerUsermode: vmpSettings.checkDebugger,
        debuggerKernelmode: vmpSettings.checkKernelDebugger,
        vmDetection: vmpSettings.checkVirtualMachine,
        stripDebug: vmpSettings.stripDebugInfo,
        stripRelocations: vmpSettings.stripRelocations,
        vmComplexity: vmpSettings.vmComplexity,
        vmVersion: vmpSettings.vmVersion ?? 0,
        vmInstances: result.raw.vmInstances ?? 1,
        vmSectionName: vmpSettings.vmCodeSectionName || '.cipher',
        messages: vmpSettings.messages ? {
          debugger: vmpSettings.messages.debugger || '',
          virtualTools: vmpSettings.messages.virtualTools || '',
          corrupted: vmpSettings.messages.corrupted || '',
          serialRequired: vmpSettings.messages.serialRequired || '',
          hwidMismatch: vmpSettings.messages.hwidMismatch || '',
        } : undefined,
      }));

      // Set debugger mode based on parsed settings
      if (vmpSettings.checkKernelDebugger) {
        setDebuggerMode('kernelmode');
      } else if (vmpSettings.checkDebugger) {
        setDebuggerMode('usermode');
      } else {
        setDebuggerMode('none');
      }

      // Convert procedures to selectedFunctions format
      if (vmpSettings.procedures && vmpSettings.procedures.length > 0) {
        const imported: SelectedFunction[] = vmpSettings.procedures.map((proc) => {
          // Try to parse mapAddress as hex RVA if it starts with 0x
          let rva = 0;
          if (proc.mapAddress.startsWith('0x') || proc.mapAddress.startsWith('0X')) {
            const parsed = parseInt(proc.mapAddress, 16);
            if (!isNaN(parsed)) {
              rva = parsed;
            }
          }
          
          return {
            name: proc.mapAddress,
            demangledName: proc.mapAddress,
            rva, // Use parsed RVA or 0 if it's a function name
            protectionType: (proc.compilationType === 2 ? 'ultra' : proc.compilationType === 1 ? 'mutation' : 'virtualization') as ProtectionType,
            complexity: proc.complexity || 50,
          };
        });
        setSelectedFunctions(imported);
      }

      // Show success feedback
      console.log('Imported VMP config:', raw.inputFileName, 'with', vmpSettings.procedures.length, 'procedures');
    } catch (err) {
      console.error('Failed to import VMP config:', err);
      setVmpImportError(err instanceof Error ? err.message : 'Failed to parse VMP config file');
    } finally {
      setVmpImporting(false);
      // Reset file input
      if (vmpFileInputRef.current) {
        vmpFileInputRef.current.value = '';
      }
    }
  };

  // Format file size helper
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format hex address
  const formatHex = (value: number): string => {
    return '0x' + value.toString(16).toUpperCase().padStart(8, '0');
  };

  // Toggle DLL expansion in imports
  const toggleDllExpansion = (dllName: string) => {
    setExpandedDlls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dllName)) {
        newSet.delete(dllName);
      } else {
        newSet.add(dllName);
      }
      return newSet;
    });
  };

  // Handle section exclusion toggle
  const toggleSectionExclusion = (sectionName: string, field: 'excludeFromPacking' | 'excludeFromProtection') => {
    setSettings(prev => {
      const existing = prev.segmentExclusions?.find(s => s.name === sectionName);
      if (existing) {
        const updated = prev.segmentExclusions?.map(s => 
          s.name === sectionName ? { ...s, [field]: !s[field] } : s
        ) || [];
        // Remove if both are false
        const filtered = updated.filter(s => s.excludeFromPacking || s.excludeFromProtection);
        return { ...prev, segmentExclusions: filtered };
      } else {
        return {
          ...prev,
          segmentExclusions: [
            ...(prev.segmentExclusions || []),
            { name: sectionName, [field]: true }
          ]
        };
      }
    });
  };

  // Check if section is excluded
  const isSectionExcluded = (sectionName: string, field: 'excludeFromPacking' | 'excludeFromProtection'): boolean => {
    const exclusion = settings.segmentExclusions?.find(s => s.name === sectionName);
    return exclusion?.[field] || false;
  };

  // Filter imports by search query
  const filteredImports = peAnalysis?.imports.filter(imp => 
    importSearchQuery === '' ||
    imp.dllName.toLowerCase().includes(importSearchQuery.toLowerCase()) ||
    imp.functions.some(f => f.name.toLowerCase().includes(importSearchQuery.toLowerCase()))
  ) || [];

  // Filter exports by search query
  const filteredExports = peAnalysis?.exports.filter(exp =>
    exportSearchQuery === '' ||
    exp.name.toLowerCase().includes(exportSearchQuery.toLowerCase())
  ) || [];

  // Helper to highlight search matches
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) return text;
    
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => {
      // Check if this part matches (case-insensitive comparison)
      const isMatch = part.toLowerCase() === query.toLowerCase();
      return isMatch ? (
        <mark key={i} className="bg-cyan-500/40 text-cyan-200 rounded px-0.5 font-semibold">{part}</mark>
      ) : (
        <span key={i}>{part}</span>
      );
    });
  };

  // Auto-expand DLLs that have matching functions when searching
  useEffect(() => {
    if (importSearchQuery && peAnalysis) {
      const matchingDlls = peAnalysis.imports
        .filter(imp => 
          imp.functions.some(f => f.name.toLowerCase().includes(importSearchQuery.toLowerCase()))
        )
        .map(imp => imp.dllName);
      
      if (matchingDlls.length > 0) {
        setExpandedDlls(new Set(matchingDlls));
      }
    }
  }, [importSearchQuery, peAnalysis]);

  const CheckboxOption = ({ 
    label, 
    checked, 
    onChange,
    disabled = false,
    icon: Icon,
  }: { 
    label: string; 
    checked: boolean; 
    onChange: () => void;
    disabled?: boolean;
    icon?: React.ComponentType<{ className?: string }>;
  }) => (
    <label className={clsx(
      'flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-300 group',
      'bg-slate-800/30 border hover-lift',
      disabled ? 'opacity-40 cursor-not-allowed' : '',
      checked 
        ? 'border-cyan-500/30 bg-cyan-500/5' 
        : 'border-slate-700/50 hover:border-slate-600'
    )}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only"
      />
      <div className={clsx(
        'w-5 h-5 rounded-md flex items-center justify-center transition-all duration-300',
        checked 
          ? 'bg-gradient-to-r from-cyan-500 to-purple-500 shadow-glow-sm' 
          : 'border-2 border-slate-600 group-hover:border-slate-500'
      )}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      {Icon && (
        <Icon className={clsx(
          'w-4 h-4 transition-colors',
          checked ? 'text-cyan-400' : 'text-slate-500'
        )} />
      )}
      <span className={clsx(
        'font-medium transition-colors',
        checked ? 'text-white' : 'text-slate-400'
      )}>{label}</span>
    </label>
  );

  const RadioOption = ({ 
    label, 
    checked, 
    onChange,
    icon: Icon,
  }: { 
    label: string; 
    checked: boolean; 
    onChange: () => void;
    icon?: React.ComponentType<{ className?: string }>;
  }) => (
    <label className={clsx(
      'flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-300 group',
      'bg-slate-800/30 border hover-lift',
      checked 
        ? 'border-cyan-500/30 bg-cyan-500/5' 
        : 'border-slate-700/50 hover:border-slate-600'
    )}>
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <div className={clsx(
        'w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300',
        checked 
          ? 'border-2 border-cyan-500' 
          : 'border-2 border-slate-600 group-hover:border-slate-500'
      )}>
        {checked && <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500" />}
      </div>
      {Icon && (
        <Icon className={clsx(
          'w-4 h-4 transition-colors',
          checked ? 'text-cyan-400' : 'text-slate-500'
        )} />
      )}
      <span className={clsx(
        'font-medium transition-colors',
        checked ? 'text-white' : 'text-slate-400'
      )}>{label}</span>
    </label>
  );

  const steps = [
    { id: 'upload', label: 'Upload', icon: Upload },
    { id: 'analyzing', label: 'Analyze', icon: Binary },
    { id: 'configure', label: 'Configure', icon: Shield },
    { id: 'processing', label: 'Process', icon: Zap },
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="opacity-0-init animate-fade-in-up">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2 font-mono">
            <Shield className="w-4 h-4 text-cyan-500" />
            <span>Create Build</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">New Build</h1>
          <p className="text-slate-400 text-sm sm:text-base">Upload and protect your binary</p>
        </div>

        {/* Progress steps */}
        <div className="flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 rounded-xl sm:rounded-2xl bg-slate-800/30 border border-slate-700/50 opacity-0-init animate-fade-in-up animation-delay-100 overflow-x-auto">
          {steps.map((s, i) => {
            const isActive = step === s.id;
            const isPast = steps.findIndex(st => st.id === step) > i;
            
            return (
              <React.Fragment key={s.id}>
                <div className={clsx(
                  'flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl transition-all flex-1 min-w-0',
                  isActive ? 'bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20' : '',
                  isPast ? 'text-cyan-400' : isActive ? 'text-white' : 'text-slate-500'
                )}>
                  <div className={clsx(
                    'w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs sm:text-sm font-bold transition-all flex-shrink-0',
                    isActive 
                      ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white' 
                      : isPast 
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : 'bg-slate-700 text-slate-500'
                  )}>
                    {isPast ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : i + 1}
                  </div>
                  <div className="hidden sm:block min-w-0">
                    <p className="font-medium text-sm truncate">{s.label}</p>
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <div className={clsx(
                    'w-4 sm:w-8 h-px flex-shrink-0',
                    isPast ? 'bg-cyan-500' : 'bg-slate-700'
                  )} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Error message */}
        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 backdrop-blur-sm opacity-0-init animate-fade-in-up">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <X className="w-4 h-4 text-red-400" />
              </div>
              <p className="text-red-400 flex-1">{error}</p>
              <button 
                onClick={() => setError(null)}
                className="text-red-400/50 hover:text-red-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Upload step */}
        {step === 'upload' && (
          <div className="space-y-6">
            {/* Binary Upload */}
            <Card title="Upload Binary" className="opacity-0-init animate-fade-in-up animation-delay-200">
              {!file ? (
                <div
                  className={clsx(
                    'border-2 border-dashed rounded-xl sm:rounded-2xl p-6 sm:p-12 text-center transition-all duration-300 cursor-pointer',
                    dragActive 
                      ? 'border-cyan-500 bg-cyan-500/5 shadow-[0_0_30px_rgba(15,240,252,0.1)]' 
                      : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800/30'
                  )}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept=".exe,.dll,.sys"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                  />
                  
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center border border-slate-700/50">
                    <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-slate-500" />
                  </div>
                  <p className="text-base sm:text-lg text-white mb-2 font-medium">
                    Drop your binary here
                  </p>
                  <p className="text-slate-500 mb-4">
                    or click to browse your files
                  </p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50">
                    <FileCode className="w-4 h-4 text-purple-400" />
                    <span className="text-slate-400 text-sm font-mono">.exe, .dll, .sys</span>
                    <span className="text-slate-600">•</span>
                    <span className="text-slate-500 text-sm">max 100MB</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{file.name}</p>
                    <p className="text-sm text-slate-400">
                      {(file.size / 1024 / 1024).toFixed(2)} MB • Ready to upload
                    </p>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </Card>

            {/* PDB Upload (Optional) */}
            <Card 
              title="Upload PDB (Optional)" 
              subtitle="Upload the corresponding PDB file to enable function-level protection"
              className="opacity-0-init animate-fade-in-up animation-delay-300"
            >
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20 flex-shrink-0">
                  <FileSymlink className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  {pdbFile ? (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{pdbFile.name}</p>
                        <p className="text-sm text-slate-500">
                          {(pdbFile.size / 1024 / 1024).toFixed(2)} MB • Ready to upload
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setPdbFile(null);
                          setPdbUploaded(false);
                          setPdbFunctionsCount(null);
                        }}
                        className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">
                      Select a .pdb file to enable function-level virtualization and mutation
                    </p>
                  )}
                </div>
                {!pdbFile && (
                  <label className="cursor-pointer flex-shrink-0">
                    <input
                      type="file"
                      accept=".pdb"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setPdbFile(e.target.files[0]);
                        }
                      }}
                    />
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20 transition-colors">
                      <Upload className="w-4 h-4" />
                      Select PDB
                    </span>
                  </label>
                )}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                The PDB file allows you to select specific functions for protection. Without it, you can still apply global protection settings.
              </p>
            </Card>

            {/* Start Upload Button */}
            {file && (
              <div className="opacity-0-init animate-fade-in-up animation-delay-400">
                <Button
                  onClick={handleStartUpload}
                  loading={loading}
                  glow
                  className="w-full"
                >
                  <Zap className="w-4 h-4" />
                  Start Upload {pdbFile && '& Analyze'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Analyzing step - Just a loading screen */}
        {step === 'analyzing' && (
          <Card variant="bordered" className="opacity-0-init animate-fade-in-up animation-delay-200">
            <div className="text-center py-16">
          <div className="space-y-6">
                {/* Animated loading spinner */}
                <div className="relative w-24 h-24 mx-auto">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10" />
                  <div className="absolute inset-0 rounded-2xl border-2 border-slate-700" />
                  <div className="absolute inset-0 rounded-2xl border-2 border-t-cyan-500 border-r-purple-500 border-b-pink-500 border-l-transparent animate-spin" style={{ animationDuration: '1.5s' }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Binary className="w-10 h-10 text-cyan-400 animate-pulse" />
                  </div>
                </div>
                
                {/* File info */}
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Analyzing Binary</h2>
                  <p className="text-slate-400 mb-4">Extracting PE structure and metadata...</p>
                  
                  <div className="inline-flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-800/50 border border-slate-700/50">
                    <FileCode className="w-5 h-5 text-cyan-400" />
                    <div className="text-left">
                      <p className="font-medium text-white text-sm truncate max-w-xs">{file?.name}</p>
                      <p className="text-xs text-slate-500 font-mono">
                        {file && (file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                  <span className="font-mono uppercase tracking-wider">Processing...</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Configure step */}
        {step === 'configure' && (
          <div className="space-y-6">
            {/* Selected file info */}
            <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 flex items-center gap-4 opacity-0-init animate-fade-in-up animation-delay-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center border border-cyan-500/20">
                <FileCode className="w-6 h-6 text-cyan-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{file?.name}</p>
                <p className="text-sm text-slate-500 font-mono">
                  {peAnalysis && `${peAnalysis.is64Bit ? '64-bit' : '32-bit'} ${peAnalysis.isDLL ? 'DLL' : 'EXE'} • ${formatBytes(peAnalysis.fileSize)}`}
                </p>
              </div>
              <span className="badge badge-completed">Ready</span>
            </div>

            {/* Engine Selection */}
            <Card title="Protection Engine" subtitle="Select the protection engine to use" className="opacity-0-init animate-fade-in-up animation-delay-150">
              {enginesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                  <span className="ml-3 text-slate-400">Loading engines...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {Object.values(ENGINE_DISPLAY).map((engine) => {
                    const available = isEngineAvailable(engine.id);
                    const versionCount = getVersionsForEngine(engine.id).length;
                    return (
                      <button
                        key={engine.id}
                        onClick={() => available && handleEngineChange(engine.id)}
                        disabled={!available}
                        className={clsx(
                          'relative p-5 rounded-xl border transition-all duration-300 text-left group',
                          !available && 'opacity-50 cursor-not-allowed',
                          selectedEngine === engine.id && available
                            ? 'border-cyan-500/50 bg-cyan-500/5 shadow-[0_0_20px_rgba(15,240,252,0.1)]'
                            : 'border-slate-700/50 hover:border-slate-600 bg-slate-800/30'
                        )}
                      >
                        {selectedEngine === engine.id && available && (
                          <div className="absolute top-3 right-3">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          </div>
                        )}
                        {!available && (
                          <div className="absolute top-3 right-3">
                            <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-400">
                              Not installed
                            </span>
                          </div>
                        )}
                        <div className="mb-3 h-8 flex items-center">{engine.icon}</div>
                        <h3 className={clsx(
                          'font-bold text-lg mb-1 transition-colors',
                          selectedEngine === engine.id && available ? 'text-white' : 'text-slate-300'
                        )}>
                          {engine.name}
                        </h3>
                        <p className="text-sm text-slate-500">{engine.description}</p>
                        {available && (
                          <p className="text-xs text-cyan-400/70 mt-2">
                            {versionCount} version{versionCount !== 1 ? 's' : ''} available
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Version Selection Dropdown */}
            {currentVersions.length > 0 && (
              <div className={clsx(
                "opacity-0-init animate-fade-in-up animation-delay-175 relative",
                versionDropdownOpen && "z-[100]"
              )}>
                <label className="block text-sm font-medium text-slate-400 mb-2 ml-1">
                  Select Version
                </label>
                <button
                  onClick={() => setVersionDropdownOpen(!versionDropdownOpen)}
                  onBlur={() => setTimeout(() => setVersionDropdownOpen(false), 150)}
                  className={clsx(
                    'w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-200',
                    versionDropdownOpen
                      ? 'border-cyan-500/50 bg-slate-800/80 shadow-[0_0_20px_rgba(15,240,252,0.1)]'
                      : 'border-slate-700/50 bg-slate-800/50 hover:border-slate-600'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center border border-slate-700/50">
                      <div className="scale-75">{currentEngineDisplay.icon}</div>
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-white">{currentEngineDisplay.name}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-cyan-400 font-mono">v{selectedVersion}</span>
                        {selectedVersion === currentVersions[0] && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400 font-medium">
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className={clsx(
                    'w-5 h-5 text-slate-400 transition-transform duration-200',
                    versionDropdownOpen && 'rotate-180 text-cyan-400'
                  )} />
                </button>

                {/* Dropdown Menu */}
                {versionDropdownOpen && (
                  <div className="absolute z-[100] top-full left-0 right-0 mt-2 rounded-xl border border-slate-700/50 bg-slate-900 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      {currentVersions.map((version, index) => (
                        <button
                          key={version}
                          onClick={() => {
                            setSelectedVersion(version);
                            setVersionDropdownOpen(false);
                          }}
                          className={clsx(
                            'w-full flex items-center justify-between px-4 py-3 transition-colors text-left',
                            selectedVersion === version
                              ? 'bg-cyan-500/10 text-white'
                              : 'hover:bg-slate-800/50 text-slate-300'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className={clsx(
                              'w-2 h-2 rounded-full transition-colors',
                              selectedVersion === version ? 'bg-cyan-500 shadow-[0_0_6px_rgba(15,240,252,0.6)]' : 'bg-slate-600'
                            )} />
                            <span className="font-mono">v{version}</span>
                            {index === 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400 font-medium">
                                Default
                              </span>
                          )}
                        </div>
                        {selectedVersion === version && (
                          <Check className="w-4 h-4 text-cyan-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              </div>
            )}

            {/* Configuration Tabs */}
            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 p-1.5 rounded-xl bg-slate-800/30 border border-slate-700/50 opacity-0-init animate-fade-in-up animation-delay-175">
              <button
                onClick={() => setActiveConfigTab('analysis')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base',
                  activeConfigTab === 'analysis'
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                )}
              >
                <Binary className="w-4 h-4" />
                <span className="hidden sm:inline">Binary Analysis</span>
                <span className="sm:hidden">Analysis</span>
                {peAnalysis && (
                  <span className={clsx(
                    'text-xs px-2 py-0.5 rounded-full',
                    activeConfigTab === 'analysis'
                      ? 'bg-cyan-500/30 text-cyan-300'
                      : 'bg-slate-700 text-slate-400'
                  )}>
                    {peAnalysis.sections.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveConfigTab('functions')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base',
                  activeConfigTab === 'functions'
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                )}
              >
                <Code className="w-4 h-4" />
                Functions
                {pdbUploaded && pdbFunctionsCount !== null && (
                  <span className={clsx(
                    'text-xs px-2 py-0.5 rounded-full',
                    activeConfigTab === 'functions'
                      ? 'bg-cyan-500/30 text-cyan-300'
                      : 'bg-slate-700 text-slate-400'
                  )}>
                    {selectedFunctions.length > 0 ? selectedFunctions.length : pdbFunctionsCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveConfigTab('protection')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base',
                  activeConfigTab === 'protection'
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                )}
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Protection</span>
                <span className="sm:hidden">Protect</span>
              </button>
            </div>

            {/* Binary Analysis Tab Content */}
            {activeConfigTab === 'analysis' && (
              <Card 
                title="Binary Analysis" 
                subtitle="Detailed information about the uploaded PE file"
                className="opacity-0-init animate-fade-in-up animation-delay-200"
              >
                {peLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-4">
                      <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-2 border-slate-700" />
                        <div className="absolute inset-0 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Binary className="w-6 h-6 text-cyan-400" />
                        </div>
                      </div>
                      <p className="text-slate-400">Loading analysis data...</p>
                    </div>
                  </div>
                ) : peError ? (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-3">
                      <XCircle className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="text-red-400 font-medium">Analysis Failed</p>
                        <p className="text-red-400/70 text-sm">{peError}</p>
                      </div>
                    </div>
                  </div>
                ) : !peAnalysis ? (
                  <div className="text-center py-12 text-slate-500">
                    <Binary className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No analysis data available</p>
                  </div>
                ) : (
                  <>
                {/* Analysis Sub-Tabs */}
                <div className="flex gap-1 p-1 rounded-xl bg-slate-800/50 border border-slate-700/50 mb-6">
                  {[
                    { id: 'overview', label: 'Overview', icon: Info, count: null },
                    { id: 'sections', label: 'Sections', icon: Layers, count: peAnalysis.sections.length },
                    { id: 'imports', label: 'Imports', icon: ArrowDownToLine, count: peAnalysis.imports.length },
                    { id: 'exports', label: 'Exports', icon: ArrowUpFromLine, count: peAnalysis.exports.length },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveAnalysisTab(tab.id as typeof activeAnalysisTab)}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-200',
                        activeAnalysisTab === tab.id
                          ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30'
                          : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                      )}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      {tab.count !== null && (
                        <span className={clsx(
                          'text-xs px-1.5 py-0.5 rounded-full',
                          activeAnalysisTab === tab.id
                            ? 'bg-cyan-500/30 text-cyan-300'
                            : 'bg-slate-700 text-slate-400'
                        )}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Overview Tab */}
                {activeAnalysisTab === 'overview' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Binary className="w-3 h-3" />
                          <span>Architecture</span>
                        </div>
                        <p className="font-mono text-white">{peAnalysis.is64Bit ? '64-bit' : '32-bit'}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Package className="w-3 h-3" />
                          <span>Type</span>
                        </div>
                        <p className="font-mono text-white">{peAnalysis.isDLL ? 'DLL' : 'EXE'}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <HardDrive className="w-3 h-3" />
                          <span>File Size</span>
                        </div>
                        <p className="font-mono text-white">{formatBytes(peAnalysis.fileSize)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Cpu className="w-3 h-3" />
                          <span>Machine</span>
                        </div>
                        <p className="font-mono text-white text-sm truncate">{peAnalysis.header.machine}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                          <Info className="w-4 h-4" />
                          PE Header
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between p-2 rounded bg-slate-800/30">
                            <span className="text-slate-500 text-sm">Entry Point</span>
                            <span className="font-mono text-cyan-400 text-sm">{formatHex(peAnalysis.header.entryPoint)}</span>
                          </div>
                          <div className="flex justify-between p-2 rounded bg-slate-800/30">
                            <span className="text-slate-500 text-sm">Image Base</span>
                            <span className="font-mono text-cyan-400 text-sm">{formatHex(peAnalysis.header.imageBase)}</span>
                          </div>
                          <div className="flex justify-between p-2 rounded bg-slate-800/30">
                            <span className="text-slate-500 text-sm">Subsystem</span>
                            <span className="font-mono text-white text-sm">{peAnalysis.header.subsystem}</span>
                          </div>
                          <div className="flex justify-between p-2 rounded bg-slate-800/30 group cursor-help">
                            <span className="text-slate-500 text-sm">Timestamp</span>
                            <span 
                              className="font-mono text-white text-sm"
                              title={`Raw: 0x${Math.floor(new Date(peAnalysis.header.timestamp).getTime() / 1000).toString(16).toUpperCase()}`}
                            >
                              <span className="group-hover:hidden">
                                {new Date(peAnalysis.header.timestamp).toLocaleString()}
                              </span>
                              <span className="hidden group-hover:inline text-cyan-400">
                                0x{Math.floor(new Date(peAnalysis.header.timestamp).getTime() / 1000).toString(16).toUpperCase()}
                              </span>
                            </span>
                          </div>
                          <div className="flex justify-between p-2 rounded bg-slate-800/30">
                            <span className="text-slate-500 text-sm">Checksum</span>
                            <span className="font-mono text-white text-sm">{formatHex(peAnalysis.header.checksum)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Characteristics
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {peAnalysis.header.characteristicFlags.map((flag) => (
                            <span
                              key={flag}
                              className="px-2 py-1 rounded text-xs font-mono bg-slate-700/50 text-slate-300 border border-slate-600/50"
                            >
                              {flag}
                            </span>
                          ))}
                          {peAnalysis.header.dllCharacteristicFlags.map((flag) => (
                            <span
                              key={flag}
                              className="px-2 py-1 rounded text-xs font-mono bg-purple-500/10 text-purple-300 border border-purple-500/20"
                            >
                              {flag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* SHA1 Hash */}
                    <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 text-sm">SHA1</span>
                        <span className="font-mono text-slate-300 text-xs select-all">{peAnalysis.sha1}</span>
                      </div>
                    </div>
                    {/* Signature Info */}
                    {peAnalysis.isSigned && peAnalysis.signatureInfo && (
                      <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                            <Shield className="w-4 h-4 text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-emerald-400 font-medium text-sm">Digitally Signed</p>
                            <p className="text-white font-mono text-sm truncate" title={peAnalysis.signatureInfo.signer}>
                              {peAnalysis.signatureInfo.signer}
                            </p>
                            <p className="text-slate-500 text-xs mt-1">
                              Issued by: {peAnalysis.signatureInfo.issuer}
                            </p>
                            <p className="text-slate-500 text-xs">
                              Valid: {new Date(peAnalysis.signatureInfo.validFrom).toLocaleDateString()} - {new Date(peAnalysis.signatureInfo.validTo).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3 pt-2">
                      <div 
                        className={clsx(
                          'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
                          peAnalysis.isSigned
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-slate-800/50 text-slate-500 border border-slate-700/50'
                        )}
                        title={peAnalysis.signatureInfo?.signer}
                      >
                        <Shield className="w-3.5 h-3.5" />
                        {peAnalysis.isSigned ? 'Signed' : 'Not Signed'}
                      </div>
                      <div className={clsx(
                        'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
                        peAnalysis.hasResources
                          ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                          : 'bg-slate-800/50 text-slate-500 border border-slate-700/50'
                      )}>
                        <Package className="w-3.5 h-3.5" />
                        Resources
                      </div>
                      <div className={clsx(
                        'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
                        peAnalysis.hasDebugInfo
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          : 'bg-slate-800/50 text-slate-500 border border-slate-700/50'
                      )}>
                        <Bug className="w-3.5 h-3.5" />
                        Debug Info
                      </div>
                      <div className={clsx(
                        'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
                        peAnalysis.hasRelocations
                          ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                          : 'bg-slate-800/50 text-slate-500 border border-slate-700/50'
                      )}>
                        <Layers className="w-3.5 h-3.5" />
                        Relocations
                      </div>
                    </div>
                  </div>
                )}

                {/* Sections Tab */}
                {activeAnalysisTab === 'sections' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-400">
                      Select sections to exclude from packing or memory protection.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-700/50">
                            <th className="text-left py-3 px-2 text-slate-400 font-medium">Section</th>
                            <th className="text-right py-3 px-2 text-slate-400 font-medium hidden md:table-cell">Virtual Size</th>
                            <th className="text-right py-3 px-2 text-slate-400 font-medium hidden lg:table-cell">Raw Size</th>
                            <th className="text-left py-3 px-2 text-slate-400 font-medium">Flags</th>
                            <th className="text-center py-3 px-2 text-slate-400 font-medium">Exclude Pack</th>
                            <th className="text-center py-3 px-2 text-slate-400 font-medium">Exclude Protect</th>
                          </tr>
                        </thead>
                        <tbody>
                          {peAnalysis.sections.map((section, idx) => (
                            <tr 
                              key={idx} 
                              className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                            >
                              <td className="py-3 px-2">
                                <span className="font-mono text-cyan-400">{section.name}</span>
                              </td>
                              <td className="py-3 px-2 text-right font-mono text-slate-300 hidden md:table-cell">
                                {formatBytes(section.virtualSize)}
                              </td>
                              <td className="py-3 px-2 text-right font-mono text-slate-300 hidden lg:table-cell">
                                {formatBytes(section.rawSize)}
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex flex-wrap gap-1">
                                  {section.flags.slice(0, 3).map((flag) => (
                                    <span
                                      key={flag}
                                      className={clsx(
                                        'px-1.5 py-0.5 rounded text-xs font-mono',
                                        flag === 'EXECUTE' ? 'bg-red-500/20 text-red-300' :
                                        flag === 'WRITE' ? 'bg-amber-500/20 text-amber-300' :
                                        flag === 'READ' ? 'bg-emerald-500/20 text-emerald-300' :
                                        'bg-slate-700/50 text-slate-400'
                                      )}
                                    >
                                      {flag.slice(0, 1)}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="py-3 px-2 text-center">
                                <button
                                  onClick={() => toggleSectionExclusion(section.name, 'excludeFromPacking')}
                                  className={clsx(
                                    'w-5 h-5 rounded flex items-center justify-center transition-all',
                                    isSectionExcluded(section.name, 'excludeFromPacking')
                                      ? 'bg-cyan-500 text-white'
                                      : 'border border-slate-600 hover:border-slate-500'
                                  )}
                                >
                                  {isSectionExcluded(section.name, 'excludeFromPacking') && (
                                    <Check className="w-3 h-3" />
                                  )}
                                </button>
                              </td>
                              <td className="py-3 px-2 text-center">
                                <button
                                  onClick={() => toggleSectionExclusion(section.name, 'excludeFromProtection')}
                                  className={clsx(
                                    'w-5 h-5 rounded flex items-center justify-center transition-all',
                                    isSectionExcluded(section.name, 'excludeFromProtection')
                                      ? 'bg-purple-500 text-white'
                                      : 'border border-slate-600 hover:border-slate-500'
                                  )}
                                >
                                  {isSectionExcluded(section.name, 'excludeFromProtection') && (
                                    <Check className="w-3 h-3" />
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {settings.segmentExclusions && settings.segmentExclusions.length > 0 && (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-sm text-amber-300">
                          <strong>{settings.segmentExclusions.length}</strong> section(s) will be excluded from protection
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Imports Tab */}
                {activeAnalysisTab === 'imports' && (
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        placeholder="Search imports..."
                        value={importSearchQuery}
                        onChange={(e) => setImportSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/25"
                      />
                    </div>
                    {filteredImports.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        {peAnalysis.imports.length === 0 ? 'No imports found' : 'No matching imports'}
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                        {filteredImports.map((imp, idx) => (
                          <div 
                            key={idx}
                            className="rounded-lg border border-slate-700/50 overflow-hidden"
                          >
                            <button
                              onClick={() => toggleDllExpansion(imp.dllName)}
                              className="w-full flex items-center justify-between p-3 bg-slate-800/30 hover:bg-slate-800/50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-3">
                                <Package className="w-4 h-4 text-purple-400" />
                                <span className="font-mono text-white">{highlightMatch(imp.dllName, importSearchQuery)}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">
                                  {imp.functions.length} functions
                                </span>
                              </div>
                              <ChevronRight className={clsx(
                                'w-4 h-4 text-slate-400 transition-transform',
                                expandedDlls.has(imp.dllName) && 'rotate-90'
                              )} />
                            </button>
                            {expandedDlls.has(imp.dllName) && (
                              <div className="p-3 bg-slate-900/50 border-t border-slate-700/50">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                                  {imp.functions.map((func, fidx) => (
                                    <div
                                      key={fidx}
                                      className="px-2 py-1 rounded text-sm font-mono text-slate-300 hover:bg-slate-800/50 truncate"
                                      title={func.name}
                                    >
                                      {highlightMatch(func.name, importSearchQuery)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Exports Tab */}
                {activeAnalysisTab === 'exports' && (
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        placeholder="Search exports..."
                        value={exportSearchQuery}
                        onChange={(e) => setExportSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/25"
                      />
                    </div>
                    {filteredExports.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        {peAnalysis.exports.length === 0 ? 'No exports found (this is normal for EXE files)' : 'No matching exports'}
                      </div>
                    ) : (
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-900">
                            <tr className="border-b border-slate-700/50">
                              <th className="text-left py-2 px-3 text-slate-400 font-medium">Name</th>
                              <th className="text-right py-2 px-3 text-slate-400 font-medium w-24">Ordinal</th>
                              <th className="text-right py-2 px-3 text-slate-400 font-medium w-32">Address</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredExports.map((exp, idx) => (
                              <tr 
                                key={idx}
                                className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                              >
                                <td className="py-2 px-3 font-mono text-cyan-400 truncate max-w-xs" title={exp.name}>
                                  {highlightMatch(exp.name, exportSearchQuery)}
                                </td>
                                <td className="py-2 px-3 text-right font-mono text-slate-400">
                                  {exp.ordinal}
                                </td>
                                <td className="py-2 px-3 text-right font-mono text-slate-300">
                                  {formatHex(exp.address)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}
              </Card>
            )}

            {/* Functions Tab Content */}
            {activeConfigTab === 'functions' && (
              <Card 
                title="Functions" 
                subtitle="Select functions to protect with virtualization or mutation"
                className="opacity-0-init animate-fade-in-up animation-delay-200"
              >
                {/* PDB Upload Section */}
                {!pdbUploaded && (
                  <div className="mb-6">
                    <div className="p-6 rounded-xl border-2 border-dashed border-slate-700 hover:border-slate-600 transition-colors">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 rounded-xl bg-slate-800/50 flex items-center justify-center mb-4 border border-slate-700/50">
                          <FileSymlink className="w-6 h-6 text-slate-500" />
                        </div>
                        <h4 className="text-white font-medium mb-2">Upload PDB File</h4>
                        <p className="text-slate-500 text-sm mb-4 max-w-md">
                          Upload the corresponding PDB file to view and select functions for protection.
                          This is optional but recommended for precise function targeting.
                        </p>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdb"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handlePdbUpload(e.target.files[0])}
                            disabled={pdbUploading}
                          />
                          <span className={clsx(
                            'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all',
                            pdbUploading
                              ? 'bg-slate-700 text-slate-400 cursor-wait'
                              : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20'
                          )}>
                            {pdbUploading ? (
                              <>
                                <Loader className="w-4 h-4 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                Select PDB File
                              </>
                            )}
                          </span>
                        </label>
                        {pdbError && (
                          <p className="text-red-400 text-sm mt-3">{pdbError}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* PDB uploaded indicator */}
                {pdbUploaded && pdbFile && (
                  <div className="mb-6 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{pdbFile.name}</p>
                        <p className="text-sm text-slate-400">
                          {pdbFunctionsCount} functions found
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setPdbFile(null);
                          setPdbUploaded(false);
                          setPdbFunctionsCount(null);
                          setSelectedFunctions([]);
                        }}
                        className="text-slate-400 hover:text-red-400 transition-colors p-2"
                        title="Remove PDB"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Functions List */}
                <FunctionsTab
                  uploadId={storedFileId || uploadId || ''}
                  hasPdb={pdbUploaded}
                  selectedFunctions={selectedFunctions}
                  onFunctionsChange={setSelectedFunctions}
                  onOpenDisassembly={handleOpenDisassembly}
                />
              </Card>
            )}

            {/* Protection Settings Tab Content */}
            {activeConfigTab === 'protection' && selectedEngine === 'vmprotect' && (
              <>
                {/* Import VMP Config Section */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-slate-700/50 bg-slate-800/30 opacity-0-init animate-fade-in-up animation-delay-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                      <FileSymlink className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Import VMProtect Config</p>
                      <p className="text-xs text-slate-400">Load settings from an existing .vmp project file</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {vmpImportError && (
                      <span className="text-xs text-red-400">{vmpImportError}</span>
                    )}
                    <input
                      ref={vmpFileInputRef}
                      type="file"
                      accept=".vmp,.xml"
                      onChange={handleVmpImport}
                      className="hidden"
                    />
                    <button
                      onClick={() => vmpFileInputRef.current?.click()}
                      disabled={vmpImporting}
                      className={clsx(
                        'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        'bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white',
                        'border border-slate-600/50 hover:border-slate-500',
                        vmpImporting && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      {vmpImporting ? (
                        <span className="flex items-center gap-2">
                          <Loader className="w-4 h-4 animate-spin" />
                          Importing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <ArrowDownToLine className="w-4 h-4" />
                          Import .vmp
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                <Card title="File Protection" subtitle="Basic protection settings for your binary" className="opacity-0-init animate-fade-in-up animation-delay-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CheckboxOption
                      label="Pack Output"
                      checked={settings.packOutput!}
                      onChange={() => toggleSetting('packOutput')}
                      icon={Lock}
                    />
                    <CheckboxOption
                      label="Import Protection"
                      checked={settings.importProtection!}
                      onChange={() => toggleSetting('importProtection')}
                      icon={Shield}
                    />
                    <CheckboxOption
                      label="Memory Protection"
                      checked={settings.memoryProtection!}
                      onChange={() => toggleSetting('memoryProtection')}
                      icon={Cpu}
                    />
                    <CheckboxOption
                      label="Resource Protection"
                      checked={settings.resourceProtection!}
                      onChange={() => toggleSetting('resourceProtection')}
                      disabled={!file?.name.toLowerCase().endsWith('.exe')}
                      icon={FileCode}
                    />
                  </div>
                </Card>

                <Card title="Output Options" subtitle="Configure the output file format" className="opacity-0-init animate-fade-in-up animation-delay-250">
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                      <div className="flex items-center gap-3 mb-2">
                        <Package className="w-5 h-5 text-cyan-400" />
                        <span className="font-medium text-white">Zip Output</span>
                      </div>
                      <p className="text-sm text-slate-500 mb-4">
                        Output will be automatically zipped with a random filename for anonymity.
                      </p>
                      <CheckboxOption
                        label="Password-protect ZIP file"
                        checked={zipPassword}
                        onChange={() => setZipPassword(!zipPassword)}
                        icon={Lock}
                      />
                      {zipPassword && (
                        <p className="mt-2 text-xs text-cyan-400/70 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5" />
                          A random password will be generated and shown after build completion
                        </p>
                      )}
                    </div>
                  </div>
                </Card>

                <Card title="Debugger Protection" subtitle="Select one mode or disable" className="opacity-0-init animate-fade-in-up animation-delay-300">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <RadioOption
                      label="Disabled"
                      checked={debuggerMode === 'none'}
                      onChange={() => setDebuggerMode('none')}
                    />
                    <RadioOption
                      label="User-mode"
                      checked={debuggerMode === 'usermode'}
                      onChange={() => setDebuggerMode('usermode')}
                      icon={Bug}
                    />
                    <RadioOption
                      label="Kernel-mode"
                      checked={debuggerMode === 'kernelmode'}
                      onChange={() => setDebuggerMode('kernelmode')}
                      icon={Bug}
                    />
                  </div>
                </Card>

                <Card title="Detection" subtitle="Runtime environment checks" className="opacity-0-init animate-fade-in-up animation-delay-350">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CheckboxOption
                      label="Virtual Machine Detection"
                      checked={settings.vmDetection!}
                      onChange={() => toggleSetting('vmDetection')}
                      icon={Cpu}
                    />
                  </div>
                </Card>

                <Card title="Virtual Machine Settings" subtitle="Configure the virtual machine protection" className="opacity-0-init animate-fade-in-up animation-delay-375">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">VM Version</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, vmVersion: 0 }))}
                          className={clsx(
                            'flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border',
                            settings.vmVersion === 0
                              ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                              : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:bg-slate-800 hover:text-slate-300'
                          )}
                        >
                          Default
                        </button>
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, vmVersion: 1 }))}
                          className={clsx(
                            'flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border',
                            settings.vmVersion === 1
                              ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                              : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:bg-slate-800 hover:text-slate-300'
                          )}
                        >
                          Compact
                        </button>
                      </div>
                      <p className="mt-1.5 text-xs text-slate-500">Compact mode produces smaller but less secure VMs</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">VM Instances</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={settings.vmInstances || 1}
                          onChange={(e) => setSettings(prev => ({ ...prev, vmInstances: parseInt(e.target.value) }))}
                          className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                        />
                        <span className="text-sm font-mono text-cyan-400 w-8 text-right">{settings.vmInstances || 1}</span>
                      </div>
                      <p className="mt-1.5 text-xs text-slate-500">Number of unique VM interpreters (1-10)</p>
                    </div>
                    {/* VM Complexity - Only shown for Default version */}
                    {settings.vmVersion === 0 && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-300 mb-2">VM Complexity</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min="1"
                            max="100"
                            value={settings.vmComplexity || 1}
                            onChange={(e) => setSettings(prev => ({ ...prev, vmComplexity: parseInt(e.target.value) }))}
                            className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                          />
                          <span className="text-sm font-mono text-cyan-400 w-12 text-right">{settings.vmComplexity || 1}%</span>
                        </div>
                        <p className="mt-1.5 text-xs text-slate-500">Higher complexity = stronger protection, slower execution (Default version only)</p>
                      </div>
                    )}
                    <div className={settings.vmVersion === 0 ? 'md:col-span-2' : ''}>
                      <label className="block text-sm font-medium text-slate-300 mb-2">VM Code Section Name</label>
                      <input
                        type="text"
                        value={settings.vmSectionName || '.cipher'}
                        onChange={(e) => setSettings(prev => ({ ...prev, vmSectionName: e.target.value }))}
                        placeholder=".cipher"
                        className="w-full px-4 py-2.5 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 font-mono text-sm"
                      />
                      <p className="mt-1.5 text-xs text-slate-500">Name of the section containing virtualized code (max 8 characters)</p>
                    </div>
                  </div>
                </Card>

                <Card title="Detection Messages" subtitle="Custom messages shown when protection is triggered" className="opacity-0-init animate-fade-in-up animation-delay-380">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">Debugger Detected</label>
                      <input
                        type="text"
                        value={settings.messages?.debugger || ''}
                        onChange={(e) => setSettings(prev => ({ 
                          ...prev, 
                          messages: { ...prev.messages, debugger: e.target.value } 
                        }))}
                        placeholder="A debugger has been detected..."
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">Virtual Machine Detected</label>
                      <input
                        type="text"
                        value={settings.messages?.virtualTools || ''}
                        onChange={(e) => setSettings(prev => ({ 
                          ...prev, 
                          messages: { ...prev.messages, virtualTools: e.target.value } 
                        }))}
                        placeholder="Virtualization tools have been detected..."
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">File Corrupted</label>
                      <input
                        type="text"
                        value={settings.messages?.corrupted || ''}
                        onChange={(e) => setSettings(prev => ({ 
                          ...prev, 
                          messages: { ...prev.messages, corrupted: e.target.value } 
                        }))}
                        placeholder="The file appears to be corrupted..."
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">Serial Number Required</label>
                      <input
                        type="text"
                        value={settings.messages?.serialRequired || ''}
                        onChange={(e) => setSettings(prev => ({ 
                          ...prev, 
                          messages: { ...prev.messages, serialRequired: e.target.value } 
                        }))}
                        placeholder="A valid serial number is required..."
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">HWID Mismatch</label>
                      <input
                        type="text"
                        value={settings.messages?.hwidMismatch || ''}
                        onChange={(e) => setSettings(prev => ({ 
                          ...prev, 
                          messages: { ...prev.messages, hwidMismatch: e.target.value } 
                        }))}
                        placeholder="Hardware ID does not match..."
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 text-sm"
                      />
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">Leave empty to use VMProtect default messages</p>
                </Card>

                <Card title="Additional Options" subtitle="Extra configuration settings" className="opacity-0-init animate-fade-in-up animation-delay-400">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CheckboxOption
                      label="Strip Debug Info"
                      checked={settings.stripDebug!}
                      onChange={() => toggleSetting('stripDebug')}
                    />
                    <CheckboxOption
                      label="Strip Relocations"
                      checked={settings.stripRelocations!}
                      onChange={() => toggleSetting('stripRelocations')}
                      disabled={!file?.name.toLowerCase().endsWith('.exe')}
                    />
                    <CheckboxOption
                      label="Enable Watermark"
                      checked={settings.useWatermark!}
                      onChange={() => toggleSetting('useWatermark')}
                      icon={Fingerprint}
                    />
                    <CheckboxOption
                      label="Shadow Stack Compatible"
                      checked={settings.shadowStack!}
                      onChange={() => toggleSetting('shadowStack')}
                    />
                    <CheckboxOption
                      label="Randomize Timestamp"
                      checked={settings.randomizeTimestamp!}
                      onChange={() => toggleSetting('randomizeTimestamp')}
                    />
                  </div>
                </Card>
              </>
            )}

            {/* Themida Settings (Placeholder) */}
            {activeConfigTab === 'protection' && selectedEngine === 'themida' && (
              <Card title="Themida Settings" subtitle="Configuration coming soon" className="opacity-0-init animate-fade-in-up animation-delay-200">
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                    <Sparkles className="w-10 h-10 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Themida Protection</h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    Themida settings are currently being configured. Default protection will be applied.
                  </p>
                </div>
              </Card>
            )}

            {/* Ryujin Settings (Placeholder) */}
            {activeConfigTab === 'protection' && selectedEngine === 'ryujin' && (
              <Card title="Ryujin Settings" subtitle="Configuration coming soon" className="opacity-0-init animate-fade-in-up animation-delay-200">
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-pink-500/10 flex items-center justify-center border border-pink-500/20">
                    <Sparkles className="w-10 h-10 text-pink-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Ryujin Protection</h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    Ryujin settings are currently being configured. Default protection will be applied.
                  </p>
                </div>
              </Card>
            )}

            <div className="flex gap-4 opacity-0-init animate-fade-in-up animation-delay-500">
              <Button variant="ghost" onClick={() => setStep('upload')}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              
              {/* Config Import/Export Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  onClick={handleExportConfig}
                  title="Export configuration to JSON file"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <label 
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 bg-transparent text-slate-300 hover:text-white hover:bg-slate-800/50 cursor-pointer"
                  title="Import configuration from JSON file"
                >
                  <FileUp className="w-4 h-4" />
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportConfig}
                    className="hidden"
                  />
                </label>
              </div>

              {storedFileId && (
                <Button 
                  variant="secondary" 
                  onClick={handleSaveSettings} 
                  loading={savingSettings}
                  className="relative"
                >
                  <Save className="w-4 h-4" />
                  {settingsSaved ? 'Saved!' : 'Save Settings'}
                  {settingsSaved && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-ping" />
                  )}
                </Button>
              )}
              <Button onClick={handleStartBuild} loading={loading} glow className="flex-1">
                <Zap className="w-4 h-4" />
                Start Protection
              </Button>
            </div>
          </div>
        )}

        {/* Processing step */}
        {step === 'processing' && (
          <Card variant="bordered" className="opacity-0-init animate-fade-in-up animation-delay-200">
            <div className="text-center py-12">
              {buildStatus === 'COMPLETED' ? (
                <div className="space-y-4">
                  <div className="relative w-24 h-24 mx-auto">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 animate-pulse" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <CheckCircle className="w-12 h-12 text-emerald-400" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Build Complete!</h2>
                    <p className="text-slate-400">Your protected binary is ready for download</p>
                    {generatedZipPassword && (
                      <div className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 inline-block">
                        <div className="flex items-center gap-2 text-amber-400">
                          <Lock className="w-4 h-4" />
                          <span className="text-sm font-medium">ZIP Password:</span>
                          <code className="font-mono text-amber-300">{generatedZipPassword}</code>
                        </div>
                      </div>
                    )}
                    <p className="text-slate-500 text-sm mt-4">Redirecting to build details...</p>
                  </div>
                </div>
              ) : buildStatus === 'FAILED' ? (
                <div className="space-y-4">
                  <div className="relative w-24 h-24 mx-auto">
                    <div className="absolute inset-0 rounded-2xl bg-red-500/10" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <XCircle className="w-12 h-12 text-red-400" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Build Failed</h2>
                    <p className="text-slate-400 mb-6">Something went wrong during protection</p>
                    <Button variant="secondary" onClick={() => navigate('/builds')}>
                      View Details
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="relative w-24 h-24 mx-auto">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10" />
                    <div className="absolute inset-0 rounded-2xl border-2 border-slate-700" />
                    <div className="absolute inset-0 rounded-2xl border-2 border-t-cyan-500 border-r-purple-500 border-b-pink-500 border-l-transparent animate-spin" style={{ animationDuration: '1.5s' }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader className="w-8 h-8 text-cyan-400 animate-pulse" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Processing...</h2>
                    <p className="text-slate-400">Your binary is being protected with {currentEngineDisplay.name}</p>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                    <span className="font-mono uppercase tracking-wider">Status: {buildStatus}</span>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Disassembly Sidebar */}
      <DisassemblySidebar
        isOpen={disasmSidebarOpen}
        onClose={() => {
          setDisasmSidebarOpen(false);
          setDisasmFunction(null);
        }}
        uploadId={storedFileId || uploadId || ''}
        func={disasmFunction}
      />
    </Layout>
  );
}
